/**
 * Course Card Component
 *
 * Individual course card component for displaying CPR course information
 * in a card layout with icon, title, description, price, duration, and CTA.
 */

import React from 'react';
import { Course } from '../../types/course';
import { Card } from '../Card';
import { Button } from '../Button';

interface CourseCardProps {
  course: Course;
  onClick: (course: Course) => void;
  className?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  onClick,
  className = '',
}) => {
  // Format price for display
  const formatPrice = (pricing: typeof course.pricing): string => {
    // Find individual pricing or use first price
    const individualPrice = pricing.find(p => p.type === 'individual') || pricing[0];
    if (!individualPrice) return 'Contact for pricing';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(individualPrice.amount);
  };

  return (
    <Card
      variant="brutalist"
      className={`transition-all duration-300 ease-out hover:shadow-xl hover:-translate-y-2 cursor-pointer gpu-accelerated group ${className}`}
    >
      <div onClick={() => onClick(course)} className="space-y-4">
        {/* Icon and Title */}
        <div className="flex items-start space-x-3">
          <div className="text-4xl flex-shrink-0 card-icon-rotate">
            {course.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="heading-card transition-colors duration-300 group-hover:text-amaranth_purple-600">
              {course.name}
            </h3>
            <p className="label-text text-gray-600">
              {course.tagline}
            </p>
          </div>
        </div>

        {/* Short Description */}
        <p className="body-text text-gray-700 min-h-[80px]">
          {course.shortDescription}
        </p>

        {/* Certification Badge */}
        <div className="flex items-center flex-wrap gap-2">
          <span className={`px-3 py-1.5 border-2 border-amaranth_purple-500 bg-amaranth_purple-50 label-text font-bold transition-all duration-200 hover:bg-amaranth_purple-100 hover:scale-105`}>
            {course.certification}
          </span>
          {course.featured && (
            <span className="px-3 py-1.5 border-2 border-navajo_white-500 bg-navajo_white-200 label-text font-bold transition-all duration-200 hover:bg-navajo_white-300 hover:scale-105">
              Featured
            </span>
          )}
        </div>

        {/* Course Details */}
        <div className="grid grid-cols-2 gap-4 border-t-4 border-b-4 border-gray-300 py-4 transition-colors duration-300 group-hover:border-indigo_dye-500">
          <div className="text-center">
            <div className="label-text text-gray-500 mb-1">Duration</div>
            <div className="font-sans font-bold text-lg text-gray-900">{course.duration}</div>
          </div>
          <div className="text-center">
            <div className="label-text text-gray-500 mb-1">Valid For</div>
            <div className="font-sans font-bold text-lg text-gray-900">{course.certificationValidity}</div>
          </div>
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="font-display text-4xl font-bold text-rich_black-500">
              {formatPrice(course.pricing)}
            </div>
            <div className="label-text text-gray-500">
              per person
            </div>
          </div>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onClick(course);
            }}
            variant="primary"
            size="md"
          >
            Learn More
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default CourseCard;
